import { fitTitle, fitDescription } from "@/lib/seo-text";
import { Link, createFileRoute } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What does safe custody of a will mean?",
    a: "Most solicitors store the original signed will in a fireproof safe or deed room at no charge, and record it in a safe custody register. The executor collects it after death, on proof of death and identity. Banks and the Public Trustee of Queensland offer the same service.",
  },
  {
    q: "How do I find out where a will is held in Queensland?",
    a: "Start with the deceased's papers for a safe custody receipt or a solicitor's letter, then their bank, their accountant, and the Public Trustee of Queensland. If the firm that drew the will has closed or merged, the files usually transfer to a successor practice, and the Queensland Law Society can help identify who holds them.",
  },
  {
    q: "Can I get probate with a copy of the will?",
    a: "Only by special application. The Supreme Court presumes a will that cannot be found was destroyed with the intention of revoking it, so you must file evidence to overcome that — how the will was made, who saw it last, and what search you carried out. It is slower and more expensive than producing the original, so it is always worth searching properly first.",
  },
  {
    q: "Will another firm release the original will to you?",
    a: "Yes. On the executor's written authority a firm holding a will in safe custody will release it to the executor's solicitors. We write for it as part of the fixed fee — you do not need to deal with them yourself.",
  },
  {
    q: "Do you store wills in safe custody?",
    a: "Yes, at no charge for clients whose wills we prepare, with a written receipt and a register entry so the executor can find it later. We can also take custody of existing wills.",
  },
];

const PLACES = [
  {
    t: "The solicitor who prepared it",
    d: "The first and most likely holder. Look for a safe custody receipt, a will index card or any correspondence on a firm's letterhead in the deceased's papers.",
  },
  {
    t: "A firm that has since merged or closed",
    d: "Practices change names and amalgamate constantly. Files and safe custody packets normally transfer to the successor firm; the Queensland Law Society can help identify who took them.",
  },
  {
    t: "The Public Trustee of Queensland",
    d: "The Public Trustee prepares free wills and holds enormous numbers of originals. They will search their register on proof of death.",
  },
  {
    t: "The bank",
    d: "Safe deposit boxes and older bank safe custody services still hold wills. The bank will need the death certificate and proof you are the executor before opening anything.",
  },
  {
    t: "The accountant or financial adviser",
    d: "Often holds a copy, and almost always knows which firm the client used.",
  },
  {
    t: "At home",
    d: "Filing cabinets, a home safe, with the title deeds, or in the back of a folder with the insurance papers. Search before assuming it is lost.",
  },
];

export const Route = createFileRoute("/will-safe-custody")({
  head: () => ({
    meta: [
      { title: fitTitle("Will Safe Custody QLD | How to Find the Original Will") },
      {
        name: "description",
        content:
          fitDescription("Where original wills are held in safe custody in Queensland, how an executor gets one released, and what to do if only a copy survives. Free will storage for our clients."),
      },
      { property: "og:title", content: fitTitle(`Finding a will held in safe custody | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("How executors locate and recover an original Queensland will held by a solicitor, a bank or the Public Trustee."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/will-safe-custody" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/will-safe-custody" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: SafeCustodyPage,
});

function SafeCustodyPage() {
  return (
    <>
      <PageHero
        eyebrow="Safe custody of wills"
        title="Finding the original will"
        lead="The Supreme Court of Queensland needs the original signed will, not a copy. Here is where originals are usually held, how an executor gets one released, and what happens if only a copy survives."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">Where to look, in order</h2>
        <div className="mt-8 space-y-5">
          {PLACES.map((p) => (
            <div key={p.t} className="rounded-sm border-l-2 border-accent bg-secondary/40 p-6">
              <h3 className="font-serif text-xl text-foreground">{p.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">We will do the chasing</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Give us the name of the firm, bank or branch you think holds it and we will write for its
            release on your authority as executor. That is included in our fixed{" "}
            <Link to="/probate-queensland" className="font-semibold text-primary underline">
              probate fee
            </Link>
            . If the will genuinely cannot be found, we can advise on an application to prove a copy.
          </p>
        </div>

        <div className="mt-14">
          <ExecutorIntakeForm heading="Tell us about the will and the estate" />
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>
      </section>

      <CtaBand
        title="Not sure who holds the will?"
        body={`Call ${FIRM.phone} with the name of the deceased and the town they lived in — we will tell you where to start.`}
      />
      <div className="h-20" />
    </>
  );
}
